import {Injectable} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import {catchError, map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {SettingsService} from '../services/settings.service';
import {StorageService} from '../services/storage.service';
import {RoutingService} from '../services/routing.service';
import * as moment from 'moment';
import {of} from 'rxjs';
import {LoginModel} from '../models/login.model';
import {BehaviorSubject, Observable, Subject, Subscription} from 'rxjs';

@Injectable()
export class AuthenticationService {
  apiUrl: string;
  refreshToken: string;
  accessToken: string;
  isSent = false;
  private beforeRefreshLoginSubject = new Subject<any>();
  private beforeLoginSubject = new Subject<any>();
  private afterLoginSubject = new Subject<any>();
  private afterRefreshLoginSubject = new Subject<any>();
  private beforeLogoutSubject = new Subject<any>();
  private afterLogoutSubject = new Subject<any>();
  private currentTimeout: any;
  private updateAccessTokenSubject = new BehaviorSubject<boolean>(false);
  private updateAccessTokenSubscription: Subscription;
  beforeRefreshLogin$ = this.beforeRefreshLoginSubject.asObservable();
  beforeLogin$ = this.beforeLoginSubject.asObservable();
  beforeLogout$ = this.beforeLogoutSubject.asObservable();
  afterLogin$ = this.afterLoginSubject.asObservable();
  afterLogout$ = this.afterLogoutSubject.asObservable();
  afterRefreshLogin$ = this.afterRefreshLoginSubject.asObservable();

  constructor(private http: HttpClient,
              private settings: SettingsService,
              private storage: StorageService,
              private routingService: RoutingService) {
    this.apiUrl = this.settings.apiUrl;
  }

  getToken(auth: LoginModel) {
    return this.http.post(`${this.apiUrl}/public/authtoken`, auth).pipe(map((data: any) => {
      this.processLoginData(auth.rememberMe, data, false);
      return data;

    }));
  }

  private processLoginData(rememberMe: boolean, data: any, isRefresh: boolean) {
    isRefresh ? this.beforeRefreshLoginSubject.next() : this.beforeLoginSubject.next();
    const user: any = {
      name: `${data.first_name} ${data.last_name}`,
      email: data.email,
      role: data.role,
      firstName: data.first_name,
      lastName: data.last_name
    };
    this.storage.rememberMe = rememberMe;
    this.storage.accessToken = data.token;
    this.accessToken = data.token;
    this.storage.refreshToken = data.refresh_token;
    this.refreshToken = data.refresh_token;
    this.storage.userId = data.id;
    this.storage.user = user;
    this.storage.image = data.image ? `${this.apiUrl}/file/${data.image}?token=${data.token}` : null;
    isRefresh ? this.afterRefreshLoginSubject.next() : this.afterLoginSubject.next();
    this.checkForTiming();
  }

  private checkForTiming() {
    const accessToken = this.accessToken || this.storage.accessToken;
    if (accessToken) {
      const now = moment();
      const rememberMe = this.storage.rememberMe;

      const lastActivity = this.storage.lastActivity;
      const helper = new JwtHelperService();
      const endTimeOfAccessToken = helper.getTokenExpirationDate(accessToken);
      const difference = moment(endTimeOfAccessToken).diff(now, 'seconds');
      const activityDifference = lastActivity ? moment(now).diff(new Date(+lastActivity), 'seconds') : 0;
      const time = 120;
      const tokenTime = 180 * 60;
      if (this.currentTimeout) {
        clearTimeout(this.currentTimeout);
      }
      if (rememberMe) {
        if (difference <= time) {
          this.updateAccessToken();
        } else {
          this.currentTimeout = setTimeout(() => {
            this.updateAccessToken();
          }, time * 1000);
        }
      } else {
        if (difference <= 0 || activityDifference >= tokenTime) {
          this.processLogoutData();
        } else {
          this.currentTimeout = setTimeout(() => {
            this.updateAccessToken();
          }, time * 1000);
        }
      }
    } else {
      this.processLogoutData();
    }
  }

  private processLogoutData(user?: any) {
    this.beforeLogoutSubject.next();
    this.storage.accessToken = null;
    this.storage.refreshToken = null;
    this.accessToken = null;
    this.refreshToken = null;
    this.storage.userId = null;
    this.storage.lastActivity = null;
    this.storage.user = null;
    this.storage.rememberMe = null;
    this.afterLogoutSubject.next();
    this.routingService.goToLoginPage();
  }

  updateAccessToken(): Observable<boolean> {
    if (!this.isSent) {
      this.isSent = true;
      let accessToken = this.accessToken || this.storage.accessToken;
      const refreshToken = this.refreshToken || this.storage.refreshToken;
      this.updateAccessTokenSubscription = this.http.post(`${this.apiUrl}/public/authtoken`, {refresh_token: refreshToken})
        .pipe(map(data => {
          this.processLoginData(this.storage.rememberMe, data, true);
          accessToken = this.storage.accessToken;
          return true;
        }))
        .pipe(catchError(error => {
          this.logout();
          this.isSent = false;
          return of(false);
        }))
        .subscribe(i => {
          this.updateAccessTokenSubject.next(i);
          this.updateAccessTokenSubscription = null;
          setTimeout(() => {
            this.isSent = false;
          }, 1000);
        });
    }
    return this.updateAccessTokenSubject;
  }


  logout() {
    this.processLogoutData();
    this.storage.clear();
  }

  forgotPassword(email: string) {
    return this.http.put(`${this.apiUrl}/public/forgot-password`, {email: email});
  }

  changePassword(data: any, token: string) {
    return this.http.put(`${this.apiUrl}/public/update-password/${token}`, data);
  }
}
